<template>
  <div class="patient-deposit-container">
    <!-- ========== 顶部区域：标题 + 说明 + 统计卡片 ========== -->
    <div class="top-section">
      <div class="title-area">
        <h2>储值管理</h2>
        <p>健康金钱包余额与变动管理，支持收预缴费、诊疗消费自动扣减，实时同步财务</p>
      </div>
      <div class="stats-cards">
        <div class="stat-item">
          <div class="stat-label">总本金余额</div>
          <div class="stat-value">¥{{ formatAmount(totalPrincipal) }}</div>
        </div>
        <div class="stat-item">
          <div class="stat-label">总健康金余额</div>
          <div class="stat-value">¥{{ formatAmount(totalHealth) }}</div>
        </div>
        <div class="stat-item">
          <div class="stat-label">本月充值</div>
          <div class="stat-value">¥{{ formatAmount(monthlyRecharge) }}</div>
        </div>
        <div class="stat-item">
          <div class="stat-label">活跃储值人数</div>
          <div class="stat-value">{{ activeCount }}</div>
        </div>
      </div>
    </div>

    <!-- ========== 下方左右布局 ========== -->
    <el-row :gutter="20" class="main-layout">
      <!-- 左侧：收预缴费 -->
      <el-col :span="8" class="left-panel">
        <el-card shadow="never" class="left-card">
          <div class="recharge-form">
            <div class="form-title">收预缴费</div>

            <el-form label-position="top" size="mini" class="form-body">
              <!-- 患者选择 -->
              <el-form-item label="患者信息">
                <el-select
                  v-model="selectedPatientId"
                  placeholder="请选择患者（手机号/姓名）"
                  filterable
                  remote
                  :remote-method="handlePatientSearch"
                  :loading="patientLoading"
                  clearable
                  style="width: 100%"
                  @change="handlePatientSelect"
                  @focus="handlePatientSearch('')"
                >
                  <el-option
                    v-for="p in patientList"
                    :key="p.id"
                    :label="`${p.name} · ${p.tel}`"
                    :value="p.id"
                  />
                </el-select>
              </el-form-item>

              <!-- 收款账户 -->
              <el-form-item label="收款账户">
                <el-select
                  v-model="paymentMethod"
                  placeholder="请选择支付方式"
                  style="width: 100%"
                  :disabled="!selectedPatientId"
                >
                  <el-option
                    v-for="item in paymentMethods"
                    :key="item.id"
                    :label="item.wayName"
                    :value="item.id"
                  />
                </el-select>
              </el-form-item>

              <!-- 预缴金额 -->
              <el-form-item label="预缴金额（本金）">
                <el-input-number
                  v-model="rechargeAmount"
                  :precision="2"
                  :min="0.01"
                  :step="10"
                  placeholder="0.00"
                  style="width: 100%"
                  :disabled="!selectedPatientId"
                  controls-position="right"
                />
              </el-form-item>

              <!-- 赠送健康金 -->
              <el-form-item label="赠送健康金">
                <el-input-number
                  v-model="giftAmount"
                  :precision="2"
                  :min="0"
                  :step="10"
                  placeholder="0"
                  style="width: 100%"
                  :disabled="!selectedPatientId"
                  controls-position="right"
                />
              </el-form-item>

              <!-- 备注（文本域） -->
              <el-form-item label="备注">
                <el-input
                  v-model="remark"
                  type="textarea"
                  :rows="2"
                  placeholder="请输入备注"
                  style="width: 100%"
                  :disabled="!selectedPatientId"
                />
              </el-form-item>
            </el-form>

            <!-- 按钮固定在底部 -->
            <div class="form-footer">
              <el-button
                type="primary"
                style="width: 100%"
                :loading="submitting"
                :disabled="!selectedPatientId || !paymentMethod || !rechargeAmount"
                @click="submitRecharge"
              >
                确认收款并写入钱包
              </el-button>
            </div>
          </div>
        </el-card>
      </el-col>

      <!-- 右侧：储值明细 -->
      <el-col :span="16" class="right-panel">
        <el-card shadow="never" class="right-card">
          <!-- 标题 + 时间筛选 -->
          <div class="detail-header">
            <div class="detail-title">储值明细</div>
            <div class="time-filter">
              <el-radio-group v-model="quickDate" size="mini" @change="handleQuickDateChange">
                <el-radio-button label="today">今天</el-radio-button>
                <el-radio-button label="yesterday">昨天</el-radio-button>
                <el-radio-button label="week">一周前</el-radio-button>
                <el-radio-button label="month">本月</el-radio-button>
                <el-radio-button label="lastMonth">上个月</el-radio-button>
              </el-radio-group>
              <el-date-picker
                v-model="timeRange"
                type="datetimerange"
                range-separator="至"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                value-format="yyyy-MM-dd HH:mm:ss"
                @change="handleDateRangeChange"
                style="width: 300px; margin-left: 15px;"
                size="mini"
              />
            </div>
          </div>

          <!-- 表格区域 -->
          <div class="table-wrapper">
            <el-table
              :data="detailData"
              border
              stripe
              size="mini"
              v-loading="detailLoading"
              max-height="100%"
              empty-text="请先选择患者"
            >
              <!-- 业务序号 - 宽度160，超出tooltip -->
              <el-table-column
                prop="bizNo"
                label="业务序号"
                width="160"
                align="center"
                show-overflow-tooltip
              />
              <!-- 患者 - 宽度160，超出tooltip -->
              <el-table-column
                prop="phrName"
                label="患者"
                width="160"
                align="center"
                show-overflow-tooltip
              />
              <el-table-column prop="bizType" label="业务类型" width="100" align="center">
                <template #default="{ row }">
                  <el-tag :type="bizTypeTagType(row.bizType)" size="mini">
                    {{ formatBizType(row.bizType) }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="type" label="类型" width="100" align="center">
                <template #default="{ row }">
                  <el-tag :type="typeTagType(row.type)" size="mini">
                    {{ formatType(row.type) }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="chgAmt" label="变化金额" width="120" align="center">
                <template #default="{ row }">
                  {{ formatAmount(row.chgAmt) }}
                </template>
              </el-table-column>
              <el-table-column prop="agoAmt" label="变化前金额" width="120" align="center">
                <template #default="{ row }">
                  {{ formatAmount(row.agoAmt) }}
                </template>
              </el-table-column>
              <el-table-column prop="afrAmt" label="变化后金额" width="120" align="center">
                <template #default="{ row }">
                  {{ formatAmount(row.afrAmt) }}
                </template>
              </el-table-column>
              <el-table-column prop="nuyState" label="冲销状态" width="100" align="center">
                <template #default="{ row }">
                  <el-tag :type="row.nuyState === 1 ? 'danger' : 'success'" size="mini" effect="plain">
                    {{ row.nuyState === 1 ? '已冲销' : '未冲销' }}
                  </el-tag>
                </template>
              </el-table-column>
              <!-- 操作人 - 显示 fullName -->
              <el-table-column prop="createId" label="操作人" width="100" align="center">
                <template #default="{ row }">
                  {{ getUserName(row.createId) }}
                </template>
              </el-table-column>
              <el-table-column prop="createTime" label="创建时间" width="160" align="center" />
              <el-table-column prop="remark" label="备注" min-width="150" show-overflow-tooltip />
              <el-table-column label="操作" width="100" align="center" fixed="right">
                <template #default>
                  <el-button type="primary" size="mini" disabled>冲销</el-button>
                </template>
              </el-table-column>
            </el-table>
          </div>

          <!-- 分页固定在底部 -->
          <div class="pagination-wrapper">
            <el-pagination
              :current-page="detailPageNum"
              :page-size="detailPageSize"
              :total="detailTotal"
              :page-sizes="[10, 20, 30]"
              layout="total, sizes, prev, pager, next, jumper"
              @size-change="handleDetailSizeChange"
              @current-change="handleDetailCurrentChange"
              background
              small
            />
          </div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import {
  getPhrWalletPage,
  phrWallet,
  getAmtById
} from "@/api/patientRechargeStatistics/patientRechargeStatistics";
import { getPatternPaymentList } from "@/api/patternPayment/patternPayment";
import { getDocphrClinicList } from "@/api/reservation/reservation";
import { AllList } from "@/api/user";
import dayjs from "dayjs";

export default {
  name: "PatientRechargeStatistics",
  data() {
    return {
      // 统计信息（模拟数据）
      totalPrincipal: 4680,
      totalHealth: 555,
      monthlyRecharge: 6500,
      activeCount: 4,

      // 左侧表单
      patientLoading: false,
      patientList: [],
      selectedPatientId: null,
      selectedPatient: null,
      paymentMethods: [],
      paymentMethod: null,
      rechargeAmount: null,
      giftAmount: 0,
      remark: "",
      submitting: false,

      // 右侧明细
      detailData: [],
      detailLoading: false,
      detailPageNum: 1,
      detailPageSize: 10,
      detailTotal: 0,
      quickDate: "month",
      timeRange: [
        dayjs().startOf("month").format("YYYY-MM-DD 00:00:00"),
        dayjs().endOf("month").format("YYYY-MM-DD 23:59:59")
      ],
      // 用户映射（createId -> fullName）
      userMap: {},

      bizTypeOptions: [
        { value: 1, label: "收入" },
        { value: 2, label: "支出" },
        { value: 3, label: "冲销" },
        { value: 4, label: "退款" }
      ],
      typeOptions: [
        { value: 1, label: "充值" },
        { value: 2, label: "赠送" }
      ]
    };
  },
  mounted() {
    this.loadPaymentMethods();
    this.loadUserList();
  },
  methods: {
    // ---------- 工具函数 ----------
    formatAmount(val) {
      if (val === null || val === undefined) return "0.00";
      const num = parseFloat(val);
      if (isNaN(num)) return val;
      return num.toFixed(2);
    },
    formatBizType(val) {
      const opt = this.bizTypeOptions.find(item => item.value === val);
      return opt ? opt.label : val;
    },
    formatType(val) {
      const opt = this.typeOptions.find(item => item.value === val);
      return opt ? opt.label : val;
    },
    bizTypeTagType(val) {
      const map = {
        1: 'success',
        2: 'warning',
        3: 'primary',
        4: 'danger'
      };
      return map[val] || '';
    },
    typeTagType(val) {
      const map = {
        1: 'success',
        2: 'warning'
      };
      return map[val] || '';
    },
    // 获取用户名（fullName）
    getUserName(id) {
      return this.userMap[id] || id || '--';
    },

    // ---------- 加载用户列表（创建人显示 fullName） ----------
    async loadUserList() {
      try {
        const res = await AllList();
        if (res.code === 200) {
          const map = {};
          (res.data || []).forEach(item => {
            // 取 fullName 字段
            map[item.id] = item.fullName || item.name || item.id;
          });
          this.userMap = map;
        } else {
          console.warn('获取用户列表失败:', res.message);
        }
      } catch (error) {
        console.error('加载用户列表出错:', error);
      }
    },

    // ---------- 患者搜索 ----------
    async handlePatientSearch(query) {
      this.patientLoading = true;
      try {
        const res = await getDocphrClinicList({ type: 1, condtion: query || "" });
        if (res.code === 200) {
          this.patientList = res.data || [];
        } else {
          this.$message.error(res.message || "搜索患者失败");
        }
      } catch (error) {
        console.error("搜索患者出错:", error);
        this.$message.error("系统异常，请联系管理员");
      } finally {
        this.patientLoading = false;
      }
    },

    // ---------- 选择患者 ----------
    async handlePatientSelect(patientId) {
      if (!patientId) {
        this.selectedPatient = null;
        this.detailData = [];
        this.detailTotal = 0;
        return;
      }

      const patient = this.patientList.find(p => p.id === patientId);
      if (!patient) {
        this.$message.warning("未找到患者信息");
        return;
      }

      try {
        const res = await getAmtById(patientId);
        if (res.code === 200 && res.data) {
          this.selectedPatient = {
            ...patient,
            rechargeBalance: res.data.recahrgeAmt || 0,
            giftBalance: res.data.handselAmt || 0
          };
        } else {
          this.$message.error(res.message || "获取患者余额失败");
          return;
        }
      } catch (error) {
        console.error("获取余额失败:", error);
        this.$message.error("获取患者余额失败，请稍后重试");
        return;
      }

      this.detailPageNum = 1;
      this.loadDetailData();
    },

    // ---------- 加载支付方式 ----------
    async loadPaymentMethods() {
      try {
        const res = await getPatternPaymentList();
        if (res.code === 200) {
          this.paymentMethods = (res.data || []).filter(item => item.status === 1);
        } else {
          this.$message.error(res.message || "获取支付方式失败");
        }
      } catch (error) {
        console.error("获取支付方式出错:", error);
        this.$message.error("系统异常，请联系管理员");
      }
    },

    // ---------- 提交充值 ----------
    async submitRecharge() {
      if (!this.selectedPatientId) {
        this.$message.warning("请选择患者");
        return;
      }
      if (!this.paymentMethod) {
        this.$message.warning("请选择收款账户");
        return;
      }
      if (!this.rechargeAmount || this.rechargeAmount <= 0) {
        this.$message.warning("预缴金额必须大于0");
        return;
      }
      if (this.giftAmount < 0) {
        this.$message.warning("赠送健康金不能为负数");
        return;
      }

      const selectedPay = this.paymentMethods.find(p => p.id === this.paymentMethod);
      if (!selectedPay || !selectedPay.uniCode) {
        this.$message.warning("支付方式缺少uniCode，请联系管理员");
        return;
      }

      const requestData = {
        phrId: this.selectedPatientId,
        handselAmt: this.giftAmount || 0,
        remark: this.remark || "",
        payWay: {
          amount: this.rechargeAmount,
          classId: selectedPay.classId,
          uniCode: selectedPay.uniCode,
          wayId: selectedPay.id
        }
      };

      this.submitting = true;
      try {
        const res = await phrWallet(requestData);
        if (res.code === 200) {
          this.$message.success("充值成功");
          // 刷新余额
          const balanceRes = await getAmtById(this.selectedPatientId);
          if (balanceRes.code === 200 && balanceRes.data) {
            this.selectedPatient.rechargeBalance = balanceRes.data.recahrgeAmt || 0;
            this.selectedPatient.giftBalance = balanceRes.data.handselAmt || 0;
          }
          this.rechargeAmount = null;
          this.giftAmount = 0;
          this.remark = "";
          this.detailPageNum = 1;
          this.loadDetailData();
        } else {
          this.$message.error(res.message || "充值失败");
        }
      } catch (error) {
        console.error("充值请求失败:", error);
        this.$message.error("系统异常，请稍后重试");
      } finally {
        this.submitting = false;
      }
    },

    // ---------- 加载储值明细 ----------
    async loadDetailData() {
      if (!this.selectedPatientId) {
        this.detailData = [];
        this.detailTotal = 0;
        return;
      }

      this.detailLoading = true;
      try {
        const params = {
          pageNum: this.detailPageNum,
          pageSize: this.detailPageSize,
          phrId: this.selectedPatientId,
          startTime: this.timeRange ? this.timeRange[0] : "",
          endTime: this.timeRange ? this.timeRange[1] : "",
          bizType: null,
          type: null
        };
        const res = await getPhrWalletPage(params);
        if (res.code === 200) {
          this.detailData = (res.data?.content || []).map(item => ({
            ...item,
            createTime: dayjs(item.createTime).format("YYYY-MM-DD HH:mm:ss")
          }));
          this.detailTotal = res.data?.totalSize || 0;
        } else {
          this.$message.error(res.message || "获取储值明细失败");
        }
      } catch (error) {
        console.error("加载储值明细失败:", error);
        this.$message.error("系统异常，请联系管理员");
      } finally {
        this.detailLoading = false;
      }
    },

    // ---------- 时间筛选 ----------
    handleQuickDateChange(value) {
      const today = new Date();
      let start, end;
      switch (value) {
        case "today":
          start = dayjs(today).format("YYYY-MM-DD 00:00:00");
          end = dayjs(today).format("YYYY-MM-DD 23:59:59");
          break;
        case "yesterday":
          const y = dayjs(today).subtract(1, "day");
          start = y.format("YYYY-MM-DD 00:00:00");
          end = y.format("YYYY-MM-DD 23:59:59");
          break;
        case "week":
          start = dayjs(today).subtract(7, "day").format("YYYY-MM-DD 00:00:00");
          end = dayjs(today).format("YYYY-MM-DD 23:59:59");
          break;
        case "month":
          start = dayjs(today).startOf("month").format("YYYY-MM-DD 00:00:00");
          end = dayjs(today).endOf("month").format("YYYY-MM-DD 23:59:59");
          break;
        case "lastMonth":
          start = dayjs(today).subtract(1, "month").startOf("month").format("YYYY-MM-DD 00:00:00");
          end = dayjs(today).subtract(1, "month").endOf("month").format("YYYY-MM-DD 23:59:59");
          break;
        default:
          return;
      }
      this.timeRange = [start, end];
      this.detailPageNum = 1;
      this.loadDetailData();
    },

    handleDateRangeChange() {
      this.quickDate = "";
      this.detailPageNum = 1;
      this.loadDetailData();
    },

    // ---------- 分页 ----------
    handleDetailSizeChange(val) {
      this.detailPageSize = val;
      this.detailPageNum = 1;
      this.loadDetailData();
    },
    handleDetailCurrentChange(val) {
      this.detailPageNum = val;
      this.loadDetailData();
    }
  }
};
</script>

<style scoped lang="less">
.patient-deposit-container {
  height: 100%;
  padding: 16px;
  background: #f0f2f5;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ===== 顶部区域 ===== */
.top-section {
  flex-shrink: 0;
  margin-bottom: 16px;

  .title-area {
    h2 {
      font-size: 20px;
      font-weight: 600;
      color: #303133;
      margin: 0 0 4px 0;
    }
    p {
      font-size: 14px;
      color: #909399;
      margin: 0 0 16px 0;
    }
  }

  .stats-cards {
    display: flex;
    gap: 16px;

    .stat-item {
      flex: 1;
      background: #fff;
      border-radius: 8px;
      padding: 16px 20px;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
      border: 1px solid #e8ecf1;

      .stat-label {
        font-size: 13px;
        color: #909399;
        margin-bottom: 6px;
      }
      .stat-value {
        font-size: 22px;
        font-weight: 600;
        color: #303133;
      }
    }
  }
}

/* ===== 下方左右布局 ===== */
.main-layout {
  flex: 1;
  height: 0;
  .left-panel,
  .right-panel {
    height: 100%;
  }

  .left-card,
  .right-card {
    height: 100%;
    border-radius: 8px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
}

/* ----- 左侧卡片 ----- */
.left-card {
  .recharge-form {
    display: flex;
    flex-direction: column;
    height: 100%;

    .form-title {
      font-size: 16px;
      font-weight: 600;
      color: #303133;
      padding-bottom: 8px;
      border-bottom: 2px solid #f56c6c;
      margin-bottom: 16px;
      flex-shrink: 0;
    }

    .form-body {
      flex: 1;
      overflow-y: auto;
      /deep/ .el-form-item {
        margin-bottom: 16px;
      }
      /deep/ .el-form-item__label {
        padding-bottom: 4px;
        font-weight: 500;
        color: #606266;
      }
      /deep/ .el-input-number {
        width: 100%;
      }
      /deep/ .el-input-number .el-input__inner {
        text-align: left;
      }
      /deep/ .el-textarea__inner {
        resize: none;
      }
    }

    .form-footer {
      flex-shrink: 0;
      padding-top: 12px;
      border-top: 1px solid #e8ecf1;
      margin-top: 8px;
    }
  }
}

/* ----- 右侧卡片 ----- */
.right-card {
  display: flex;
  flex-direction: column;

  .detail-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 12px;
    border-bottom: 1px solid #e8ecf1;
    flex-shrink: 0;

    .detail-title {
      font-size: 16px;
      font-weight: 600;
      color: #303133;
    }

    .time-filter {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 6px;
      /deep/ .el-radio-group .el-radio-button__inner {
        padding: 5px 12px;
        font-size: 12px;
      }
    }
  }

  .table-wrapper {
    flex: 1;
    margin-top: 12px;
    overflow: hidden;
    /deep/ .el-table {
      height: 100%;
      font-size: 12px;
      .el-table__body-wrapper {
        height: calc(100% - 40px);
        overflow-y: auto;
      }
    }
  }

  .pagination-wrapper {
    flex-shrink: 0;
    padding-top: 12px;
    border-top: 1px solid #e8ecf1;
    margin-top: 12px;
    text-align: right;
  }
}

/* ===== 响应式 ===== */
@media (max-width: 1200px) {
  .main-layout {
    .left-panel,
    .right-panel {
      flex: 0 0 100%;
      max-width: 100%;
      height: auto;
    }
    .left-panel {
      margin-bottom: 16px;
    }
  }
  .top-section .stats-cards {
    flex-wrap: wrap;
    .stat-item {
      flex: 0 0 calc(50% - 8px);
    }
  }
}
@media (max-width: 768px) {
  .top-section .stats-cards .stat-item {
    flex: 0 0 100%;
  }
  .right-card .detail-header {
    flex-direction: column;
    align-items: stretch;
    .time-filter {
      margin-top: 8px;
      justify-content: flex-start;
    }
  }
}
</style>